import { Edit, MoreVertical } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/components/Default/LekUI/Button";
import { Menu } from "src/components/Default/LekUI/Menu";
import { FolderColorIcon } from "src/components/Folders/FolderColorIcon";
import DefaultActions from "src/components/NewComponents/menuActions/DefaultActions";
import formatHtmlTag from "src/helpers/formatHtmlTag";
import { FolderNoteEdit } from "./FolderNoteEdit";
import { FolderNotePreview } from "./FolderNotePreview";

enum NoteMode {
	Card = "card",
	Preview = "preview",
	Edit = "edit",
}

interface NoteData {
	id: string | number;
	name?: string;
	note?: string;
	createdAt?: string;
}

interface FolderInfo {
	id: string;
	name: string;
	details?: { color?: string | null } | null;
}

interface FolderNoteCardProps {
	note: NoteData | null;
	folder?: FolderInfo | null;
}

export function FolderNoteCard({ note, folder }: FolderNoteCardProps) {
	const { t } = useTranslation();
	const [mode, setMode] = useState<NoteMode>(NoteMode.Card);

	if (!note) return null;

	if (mode === NoteMode.Preview) {
		return (
			<FolderNotePreview
				noteId={String(note.id)}
				onBack={() => setMode(NoteMode.Card)}
				onEdit={() => setMode(NoteMode.Edit)}
			/>
		);
	}

	if (mode === NoteMode.Edit) {
		return (
			<FolderNoteEdit
				noteId={String(note.id)}
				onCancel={() => setMode(NoteMode.Card)}
				onSaved={() => setMode(NoteMode.Card)}
			/>
		);
	}

	const preview = note.note ? formatHtmlTag(note.note) : "";

	return (
		<div className="content-box border-radius-8 flex flex-col gap-24 items-end">
			<div className="w-100 flex flex-col gap-8 pb-12 border-bottom-2">
				<div className="flex items-center justify-between">
					{folder && (
						<div className="flex items-center gap-8">
							<FolderColorIcon
								color={folder.details?.color}
								iconClassName="icon-small"
							/>
							<span className="size-16 text-ellipsis-1">{folder.name}</span>
						</div>
					)}
					<Menu
						icon={<MoreVertical className="icon-small" />}
						buttonTestId={`folder-note-menu-${note.id}`}
						Actions={
							<DefaultActions
								defaultItems={[
									{
										title: t("commands.edit", "Edytuj"),
										onClick: () => setMode(NoteMode.Edit),
										Icon: <Edit className="icon-small" />,
										testId: `folder-note-edit-${note.id}`,
									},
								]}
							/>
						}
					/>
				</div>
				<h3 className="m-0 size-16 text-ellipsis-1 pr-8">
					{note.name || t("note.untitled", "Notatka bez tytułu")}
				</h3>
			</div>
			<p className="m-0 size-14-light w-100 line-height-15 text-ellipsis-3">
				{preview}
		</p>
			<PrimaryButton
				onClick={() => setMode(NoteMode.Preview)}
				data-test-id={`folder-note-preview-${note.id}`}
			>
				{t("exam.notes.preview", "Podgląd")}
			</PrimaryButton>
		</div>
	);
}
